import { ErrorState } from "@/components/molecules/error-state";
import { AFTER_SAVE, differs, FormFooter } from "@/components/molecules/form-footer";
import { PageSkeleton } from "@/components/molecules/page-skeleton";
import { SettingsSection } from "@/components/molecules/settings-section";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  useAgents,
  useDeleteAgent,
  useUpdateAgent,
} from "@/features/agents/hooks/use-agents";
import type { Agent } from "@/features/agents/types";
import { useTranslation } from "@/features/utils/i18n";
import { TrashIcon, WarningIcon } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { PageLayout } from "../layout";
import { AgentForm } from "./components/agent-form";
import { AgentNotFound } from "./view";

export function EditAgentPage() {
  const { t } = useTranslation("agents");
  const { company: clientId, agentId } = useParams<{
    company: string;
    agentId: string;
  }>();
  const navigate = useNavigate();
  const { data: agents = [], isLoading, isError, refetch } = useAgents();
  const updateAgent = useUpdateAgent();
  const deleteAgent = useDeleteAgent();

  const agent = agents.find((a) => a.id === agentId);

  const [formData, setFormData] = useState<Partial<Agent>>({
    id: "",
    name: "",
    role: "",
    identity: "",
    voice: "",
    background_audio: "",
    default_greetings: "",
  });

  useEffect(() => {
    if (agent) {
      setFormData(agent);
    }
  }, [agent]);

  const handleSubmit = async () => {
    if (!agentId) return;

    try {
      await updateAgent.mutateAsync({ id: agentId, agent: formData });
      toast.success(t("agents.messages.updated"));
      navigate(`/${clientId}/agents/${agentId}`, AFTER_SAVE);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("agents.messages.updateError"),
      );
    }
  };

  const handleDelete = async () => {
    if (!agentId) return;

    try {
      await deleteAgent.mutateAsync(agentId);
      toast.success(t("agents.messages.deleted"));
      // Nothing left to save: an agent that no longer exists owes no warning.
      navigate(`/${clientId}/agents`, AFTER_SAVE);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("agents.messages.deleteError"),
      );
    }
  };

  const backTo = agent
    ? { label: agent.name, path: `/${clientId}/agents/${agentId}` }
    : { label: t("agents.edit.backToAgents"), path: `/${clientId}/agents` };

  if (isLoading) return <PageSkeleton width="narrow" blocks={["h-[32rem]"]} />;

  if (isError) {
    return (
      <PageLayout title={t("agents.edit.title")} backTo={backTo} width="narrow">
        <ErrorState onRetry={refetch} />
      </PageLayout>
    );
  }

  if (!agent) return <AgentNotFound backTo={backTo} />;

  return (
    <PageLayout
      title={t("agents.edit.editTitle", { name: agent.name })}
      description={t("agents.edit.description")}
      backTo={backTo}
      width="narrow"
    >
      <Card>
        <CardHeader>
          <CardTitle>{t("agents.edit.configuration")}</CardTitle>
        </CardHeader>
        <CardContent>
          {formData?.id && <AgentForm data={formData} onChange={setFormData} />}
        </CardContent>
      </Card>

      {/* Deleting sits apart from the form, at the bottom — the way the
          company settings end on "Supprimer l'entreprise" — rather than as a
          lone red icon floating beside the title. */}
      <SettingsSection
        tone="danger"
        title={
          <span className="flex items-center gap-2">
            <WarningIcon className="size-5" />
            {t("agents.delete.title")}
          </span>
        }
        description={t("agents.delete.description")}
      >
        <div>
          <Button
            type="button"
            variant="destructive"
            onClick={() => handleDelete()}
            disabled={deleteAgent.isPending}
            confirm={t("agents.delete.confirm", { name: agent.name })}
          >
            <TrashIcon className="size-4" />
            {t("agents.edit.deleteAgent")}
          </Button>
        </div>
      </SettingsSection>

      <FormFooter
        dirty={!!formData?.id && differs(formData, agent)}
        saving={updateAgent.isPending}
        onSave={handleSubmit}
        onCancel={() => setFormData(agent)}
      />
    </PageLayout>
  );
}
